import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Sparkles, Calendar, Loader2, Check, Edit2, Trash2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CurrentAffairsGenerator = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [count, setCount] = useState(8);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatedAffairs, setGeneratedAffairs] = useState([]);
  const [selectedAffairs, setSelectedAffairs] = useState(new Set());
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedContent, setEditedContent] = useState({});

  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const dateObj = new Date(selectedDate);
      const formattedDate = dateObj.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });

      const response = await fetch(`${API_URL}/api/current-affairs/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          date_str: formattedDate,
          count: count
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate current affairs');
      }

      const data = await response.json();
      setGeneratedAffairs(data.current_affairs || []);
      
      // Select all by default
      const allIndices = new Set(data.current_affairs.map((_, idx) => idx));
      setSelectedAffairs(allIndices);
      
      alert(`✅ Generated ${data.count} current affairs successfully!`);
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Failed to generate current affairs. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (selectedAffairs.size === 0) {
      alert('Please select at least one current affair to publish');
      return;
    }

    setPublishing(true);
    try {
      const affairsToPublish = generatedAffairs.filter((_, idx) => 
        selectedAffairs.has(idx)
      );

      const response = await fetch(`${API_URL}/api/current-affairs/publish-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          current_affairs: affairsToPublish,
          publish_date: new Date(selectedDate).toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to publish current affairs');
      }

      const data = await response.json();
      alert(`✅ Published ${data.published_count} current affairs as blog posts!`);
      
      // Clear selection and generated affairs
      setGeneratedAffairs([]);
      setSelectedAffairs(new Set());
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Failed to publish current affairs. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const toggleSelection = (index) => {
    const newSelected = new Set(selectedAffairs);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedAffairs(newSelected);
  };

  const startEditing = (index) => {
    setEditingIndex(index);
    setEditedContent({
      title: generatedAffairs[index].title,
      content: generatedAffairs[index].content,
      category: generatedAffairs[index].category
    });
  };

  const saveEdit = (index) => {
    const updated = [...generatedAffairs];
    updated[index] = {
      ...updated[index],
      ...editedContent
    };
    setGeneratedAffairs(updated);
    setEditingIndex(null);
    setEditedContent({});
  };

  const deleteAffair = (index) => {
    const updated = generatedAffairs.filter((_, idx) => idx !== index);
    setGeneratedAffairs(updated);
    
    // Update selections
    const newSelected = new Set();
    selectedAffairs.forEach(idx => {
      if (idx < index) newSelected.add(idx);
      else if (idx > index) newSelected.add(idx - 1);
    });
    setSelectedAffairs(newSelected);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
          <Sparkles className="mr-2 text-purple-600" size={32} />
          AI Current Affairs Generator
        </h1>
        <p className="text-gray-600">Generate exam-focused current affairs content with one click using AI</p>
      </div>

      {/* Generation Controls */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="mr-2" size={20} />
            Generate Current Affairs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Items
              </label>
              <input
                type="number"
                min="1"
                max="15"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 text-white"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={18} />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </div>

          {generatedAffairs.length > 0 && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 font-medium">
                ✅ Generated {generatedAffairs.length} current affairs items. Review and publish below.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Affairs List */}
      {generatedAffairs.length > 0 && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Generated Current Affairs ({generatedAffairs.length})
            </h2>
            <Button
              onClick={handlePublish}
              disabled={publishing || selectedAffairs.size === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {publishing ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Publishing...
                </>
              ) : (
                <>
                  <FileText className="mr-2" size={18} />
                  Publish Selected ({selectedAffairs.size})
                </>
              )}
            </Button>
          </div>

          <div className="space-y-4">
            {generatedAffairs.map((affair, index) => (
              <Card key={index} className={`${selectedAffairs.has(index) ? 'border-2 border-purple-500' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="flex items-center h-6 mt-1">
                      <input
                        type="checkbox"
                        checked={selectedAffairs.has(index)}
                        onChange={() => toggleSelection(index)}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {editingIndex === index ? (
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={editedContent.title}
                            onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg font-semibold"
                          />
                          <textarea
                            value={editedContent.content}
                            onChange={(e) => setEditedContent({ ...editedContent, content: e.target.value })}
                            rows={8}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <input
                            type="text"
                            value={editedContent.category}
                            onChange={(e) => setEditedContent({ ...editedContent, category: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Category"
                          />
                          <div className="flex gap-2">
                            <Button onClick={() => saveEdit(index)} className="bg-green-600 hover:bg-green-700">
                              <Check size={16} className="mr-1" />
                              Save
                            </Button>
                            <Button onClick={() => setEditingIndex(null)} variant="outline">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-xl font-bold text-gray-900 flex-1">{affair.title}</h3>
                            <span className="ml-2 px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                              {affair.category}
                            </span>
                          </div>
                          
                          <div className="prose max-w-none text-gray-700 mb-3">
                            {affair.content.split('\n').map((line, i) => (
                              <p key={i} className="mb-2">{line}</p>
                            ))}
                          </div>

                          {affair.exam_relevance && (
                            <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 mb-3">
                              <p className="text-sm text-yellow-800">
                                <strong>Exam Relevance:</strong> {affair.exam_relevance}
                              </p>
                            </div>
                          )}

                          {affair.keywords && affair.keywords.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {affair.keywords.map((keyword, i) => (
                                <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    {editingIndex !== index && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => startEditing(index)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          onClick={() => deleteAffair(index)}
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Instructions */}
      {generatedAffairs.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>📚 How It Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start">
              <span className="font-bold text-purple-600 mr-2">1.</span>
              <p className="text-gray-700">Select a date and number of current affairs items to generate</p>
            </div>
            <div className="flex items-start">
              <span className="font-bold text-purple-600 mr-2">2.</span>
              <p className="text-gray-700">Click "Generate with AI" - our AI will create exam-focused current affairs</p>
            </div>
            <div className="flex items-start">
              <span className="font-bold text-purple-600 mr-2">3.</span>
              <p className="text-gray-700">Review the generated content, edit if needed, and select items to publish</p>
            </div>
            <div className="flex items-start">
              <span className="font-bold text-purple-600 mr-2">4.</span>
              <p className="text-gray-700">Click "Publish Selected" to add them as blog posts in the "Current Affairs" category</p>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-800 text-sm">
                <strong>💡 Tip:</strong> Generated content covers National, International, Banking, Economy, Awards, Science, Sports, and Books - all relevant for SSC, Banking, and RBI exams!
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CurrentAffairsGenerator;
